import {createFileRoute, Link} from '@tanstack/react-router';
import {z} from 'zod';
import {Button} from '@/components/ui/button';
import {Footer} from '@/components/ui/footer';
import {Navbar} from '@/components/ui/navbar';
import {Typography} from '@/components/ui/typography';
import {EventListCard} from '@/components/eventListCard/eventListCard';
import {PaginationGroup} from '@/components/paginationGroup/paginationGroup';
import {getProfileName} from '@/utils/getProfileName';
import {getPublicProfileLists} from '@/server/profile';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://wayra.cc';

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 12;

const listsSearchSchema = z.object({
  page: z.number().int().positive().optional().catch(undefined),
  pageSize: z.number().int().positive().max(50).optional().catch(undefined),
});

export const Route = createFileRoute('/rider/$profileId/lists')({
  validateSearch: (search) => listsSearchSchema.parse(search),
  loaderDeps: ({search}) => ({
    page: search.page ?? DEFAULT_PAGE,
    pageSize: search.pageSize ?? DEFAULT_PAGE_SIZE,
  }),
  loader: ({params, deps}) =>
    getPublicProfileLists({
      data: {
        profileId: params.profileId,
        page: deps.page,
        pageSize: deps.pageSize,
      },
    }),
  head: ({params, loaderData}) => {
    if (!loaderData) return {meta: [{title: 'Profile not found · Wayra'}]};
    const name = getProfileName(loaderData.profile);
    const canonicalUrl = `${SITE_URL}/rider/${params.profileId}/lists`;
    return {
      meta: [
        {title: `${name}'s lists · Wayra`},
        {
          name: 'description',
          content: `${loaderData.pagination.totalCount} public lists curated by ${name}.`,
        },
      ],
      links: [
        {rel: 'canonical', href: canonicalUrl},
      ],
    };
  },
  errorComponent: PublicListsNotFound,
  component: PublicListsPage,
});

function PublicListsPage() {
  const data = Route.useLoaderData();

  if (!data) return <PublicListsNotFound />;

  const {profile, lists, pagination} = data;
  const name = getProfileName(profile);

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 flex flex-col gap-6">
        <header className="flex flex-col gap-2">
          <Link
            to="/rider/$profileId"
            params={{profileId: profile.id}}
            className="text-sm text-neutral-500 hover:text-neutral-700"
          >
            ← Back to {name}
          </Link>
          <Typography level="D4" className="italic font-black">
            {name}'s Lists
          </Typography>
          <Typography level="B3" className="text-neutral-500">
            {pagination.totalCount}{' '}
            {pagination.totalCount === 1 ? 'list' : 'lists'}
          </Typography>
        </header>

        {lists.length === 0 ? (
          <Typography level="B1" className="text-neutral-500">
            No public lists yet.
          </Typography>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {lists.map((list) => (
                <EventListCard
                  key={list.id}
                  list={list}
                  profileId={profile.id}
                />
              ))}
            </div>
            {pagination.totalPages > 1 ? (
              <PaginationGroup pagination={pagination} />
            ) : null}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
}

function PublicListsNotFound() {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Navbar />
      <div className="flex flex-1 flex-col items-center justify-center gap-4 py-12">
        <Typography level="H3">Profile not found</Typography>
        <Typography level="B1" className="text-neutral-500">
          This profile doesn&apos;t exist.
        </Typography>
        <Link to="/">
          <Button variant="outline">Back to Wayra</Button>
        </Link>
      </div>
      <Footer />
    </div>
  );
}
